/**
 * BulkEquipmentService
 * 
 * Handles batch creation of equipment and control card generation
 * Uses v2_equipment table for equipment storage
 */

import { supabase } from '@/lib/supabase/client'
import { jsPDF } from 'jspdf'
import { PDFDocument } from 'pdf-lib'
import type { EquipmentEntry, BulkCreationOptions, BulkCreationResult } from '../types'
import { getContainer } from '@/features/control-card'
import { STM_LOGO, CERT_LOGO } from '@/assets/control card'
import { uploadDocument } from '@/services/document-upload.service'
import { generatePDF as generateMTPDF } from '@/services/mtPDFTemplate'
import type { MTReportData } from '@/types/mtTypes'
import { DEFAULT_MT_EQUIPMENT } from '@/types/mtTypes'

// Extended entry type that may include product_type_id and FAL-specific fields
interface ExtendedEquipmentEntry extends EquipmentEntry {
  product_type_id?: string
  capacity?: number  // Kapasitet (kg) for FAL categories
  length?: string    // Length (m) for Fiber Sling (R2) categories
  hasHook?: boolean  // Whether fiber sling has a hook (R2 categories)
  hookId?: string    // Hook serial number/ID (R2 categories)
}

export class BulkEquipmentService {
  /**
   * Validate a single equipment entry
   */
  static validateEntry(entry: EquipmentEntry): string[] {
    const errors: string[] = []
    
    if (!entry.name.trim()) {
      errors.push('Navn er påkrevd')
    }
    
    if (!entry.serial_number.trim()) {
      errors.push('Serienummer er påkrevd')
    }
    
    if (!entry.category_id) {
      errors.push('Kategori er påkrevd')
    }
    
    // For custom categories, check customManufacturerName instead of manufacturer_id
    if (entry.isCustomCategory) {
      if (!entry.customManufacturerName?.trim()) {
        errors.push('Produsent er påkrevd')
      }
    } else {
      if (!entry.manufacturer_id) {
        errors.push('Produsent er påkrevd')
      }
    }
    
    return errors
  }

  /**
   * Check for duplicate serial numbers within the batch and in database
   */
  static async checkDuplicates(
    entries: EquipmentEntry[], 
    companyId: string
  ): Promise<Map<string, string>> {
    const duplicates = new Map<string, string>()
    const serialNumbers = entries.map(e => e.serial_number.trim().toLowerCase())
    
    // Check within batch
    const seen = new Set<string>()
    entries.forEach((entry, index) => {
      const sn = entry.serial_number.trim().toLowerCase()
      if (seen.has(sn)) {
        duplicates.set(entry.id, 'Duplikat serienummer i listen')
      }
      seen.add(sn)
    })
    
    // Check against database
    const { data: existing } = await supabase
      .from('equipment_v2')
      .select('serial_number')
      .eq('company_id', companyId)
      .in('serial_number', serialNumbers)
    
    if (existing && existing.length > 0) {
      const existingSet = new Set(existing.map(e => e.serial_number?.toLowerCase()))
      entries.forEach(entry => {
        if (existingSet.has(entry.serial_number.trim().toLowerCase())) {
          duplicates.set(entry.id, 'Serienummer finnes allerede')
        }
      })
    }
    
    return duplicates
  }

  /**
   * Look up standards for a category from standards_categories table
   * Returns comma-separated list of standard codes (e.g., "EN 361, EN 363")
   * Falls back to parent category's standards if subcategory has none
   */
  private static async getCategoryStandards(categoryId: string): Promise<string | null> {
    try {
      const { data: standards } = await supabase
        .from('standards_categories')
        .select(`
          standard:standards_v2(code, title)
        `)
        .eq('category_id', categoryId)
      
      if (standards && standards.length > 0) {
        // Extract standard codes and join them
        const standardCodes = standards
          .map((s: any) => s.standard?.code || s.standard?.title)
          .filter(Boolean)
          .join(', ')
        
        if (standardCodes) {
          return standardCodes
        }
      }
      
      // No standards found - try parent category
      const { data: catData } = await supabase
        .from('categories_v2')
        .select('parent_id')
        .eq('id', categoryId)
        .single()
      
      if (catData?.parent_id) {
        // Recursively try parent category
        return this.getCategoryStandards(catData.parent_id)
      }
      
      return null
    } catch (err) {
      console.error('Failed to fetch category standards:', err)
      return null
    }
  }

  /**
   * Generate asset number using company-specific sequence
   */
  private static async generateAssetNumber(companyId: string): Promise<string> {
    try {
      const { data: assetNumber, error } = await supabase.rpc('generate_company_asset_number', {
        company_id_param: companyId
      })
      
      if (error) {
        // Fallback with more uniqueness
        const timestamp = Date.now().toString()
        const random = Math.random().toString(36).substring(2, 8)
        return `BATCH-${timestamp}-${random}`
      }
      
      return assetNumber || `BATCH-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
    } catch {
      const timestamp = Date.now().toString()
      const random = Math.random().toString(36).substring(2, 8)
      return `BATCH-${timestamp}-${random}`
    }
  }

  /**
   * Initialize specifications from category templates
   */
  private static async initializeSpecifications(
    equipmentId: string, 
    categoryId: string, 
    userId: string
  ): Promise<void> {
    try {
      // Get specification templates for category
      let { data: templates } = await supabase
        .from('specification_templates_with_categories')
        .select('*')
        .eq('category_id', categoryId)
        .eq('is_visible', true)
        .order('display_order', { ascending: true })

      // Fallback to legacy table
      if (!templates || templates.length === 0) {
        const { data: legacyTemplates } = await supabase
          .from('specification_templates_v2')
          .select('*')
          .eq('category_id', categoryId)
          .eq('is_visible', true)
          .order('display_order', { ascending: true })
        
        templates = legacyTemplates
      }

      if (!templates || templates.length === 0) return

      // Create specification records
      const specifications = templates
        .filter(t => t.is_visible !== false)
        .map(template => {
          const spec: Record<string, unknown> = {
            equipment_id: equipmentId,
            template_id: template.id,
            spec_key: template.spec_key,
            unit_id: template.default_unit_id,
            created_by: userId,
            updated_by: userId,
            validation_status: 'pending'
          }

          // Set value based on data type
          switch (template.data_type) {
            case 'numeric':
            case 'decimal':
              spec.value_numeric = template.default_value ? parseFloat(template.default_value) : 0
              break
            case 'integer':
            case 'year':
              spec.value_integer = template.default_value ? parseInt(template.default_value) : 0
              break
            case 'boolean':
              spec.value_boolean = template.default_value === 'true'
              break
            default:
              spec.value_text = template.default_value || ''
              break
          }

          return spec
        })

      if (specifications.length > 0) {
        await supabase.from('equipment_specifications_v2').insert(specifications)
      }
    } catch {
      // Failed to initialize specifications
    }
  }

  /**
   * Create a single equipment record
   * Uses equipment_v2 table
   */
  private static async createSingleEquipment(
    entry: EquipmentEntry,
    companyId: string,
    userId: string
  ): Promise<{ success: boolean; equipment_id?: string; asset_number?: string; categoryStandards?: string; error?: string }> {
    try {
      const assetNumber = await this.generateAssetNumber(companyId)
      
      // Look up category - ID is already from categories_v2
      let categoryV2Id: string | null = null
      if (entry.category_id) {
        // First, check if this is a sling category (R1 or R3) with parts selected
        const { data: parentCat } = await supabase
          .from('categories_v2')
          .select('id, code')
          .eq('id', entry.category_id)
          .single()
        
        if (parentCat && (parentCat.code === 'R1' || parentCat.code === 'R3') && entry.slingParts) {
          // Resolve to subcategory based on parts (R1-1, R1-2, etc. or R3-1, R3-2, etc.)
          // For 5-part, use the 4-part category (most systems only have up to 4-part as subcategory)
          const partNumber = entry.slingParts > 4 ? 4 : entry.slingParts
          const subcategoryCode = `${parentCat.code}-${partNumber}`
          
          const { data: subCat } = await supabase
            .from('categories_v2')
            .select('id')
            .eq('code', subcategoryCode)
            .single()
          
          if (subCat) {
            categoryV2Id = subCat.id
          } else {
            // Fallback to parent category if subcategory not found
            categoryV2Id = parentCat.id
          }
        } else if (parentCat) {
          // Not a sling category or no parts selected - use as-is
          categoryV2Id = parentCat.id
        } else {
          // Fallback: try samleliste_categories for backwards compatibility
          const { data: samCat } = await supabase
            .from('samleliste_categories')
            .select('code, subcategory_id')
            .eq('id', entry.category_id)
            .single()
          
          if (samCat) {
            // Use subcategory_id directly if available (e.g., R4-2 for Offshore Shackles)
            if (samCat.subcategory_id) {
              categoryV2Id = samCat.subcategory_id
            } else if (samCat.code) {
              // Fallback to matching by code
              const { data: catFromCode } = await supabase
                .from('categories_v2')
                .select('id')
                .eq('code', samCat.code)
                .single()
              
              if (catFromCode) {
                categoryV2Id = catFromCode.id
              }
            }
          }
        }
      }
      
      // Samleliste uses base regulations only: Forskrift Nr 1357, Forskrift Nr 1360
      // Category-specific standards are not added for Samleliste control cards
      const categoryStandards: string | null = null
      
      // Look up manufacturer - ID is already from manufacturers_v2
      let manufacturerV2Id: string | null = null
      let manufacturerName: string | null = null
      
      // For custom categories, use the manually entered manufacturer name
      if (entry.isCustomCategory && entry.customManufacturerName?.trim()) {
        manufacturerName = entry.customManufacturerName.trim()
        // Try to find existing manufacturer by name
        const { data: existingMfr } = await supabase
          .from('manufacturers_v2')
          .select('id')
          .ilike('name', manufacturerName)
          .single()
        
        if (existingMfr) {
          manufacturerV2Id = existingMfr.id
        }
        // If not found, manufacturerV2Id stays null but we still have the name for specs
      } else if (entry.manufacturer_id) {
        // manufacturer_id is already a manufacturers_v2 ID
        const { data: mfrV2 } = await supabase
          .from('manufacturers_v2')
          .select('id, name')
          .eq('id', entry.manufacturer_id)
          .single()
        
        if (mfrV2) {
          manufacturerV2Id = mfrV2.id
          manufacturerName = mfrV2.name
        } else {
          // Fallback: try samleliste_manufacturers for backwards compatibility
          const { data: samMfr } = await supabase
            .from('samleliste_manufacturers')
            .select('name')
            .eq('id', entry.manufacturer_id)
            .single()
          
          if (samMfr?.name) {
            manufacturerName = samMfr.name
            const { data: mfrFromName } = await supabase
              .from('manufacturers_v2')
              .select('id')
              .ilike('name', samMfr.name)
              .single()
            
            if (mfrFromName) {
              manufacturerV2Id = mfrFromName.id
            }
          }
        }
      }
      
      // Build equipment data for equipment_v2 table
      const now = new Date()
      const nextInspectionDate = new Date(now)
      nextInspectionDate.setFullYear(nextInspectionDate.getFullYear() + 1) // Default 1 year interval
      
      // Parse NDT date if provided (format: dd.mm.yyyy)
      let lastNdtDate: string | null = null
      let nextNdtDate: string | null = null
      if (entry.ndtDate) {
        const parts = entry.ndtDate.split('.')
        if (parts.length === 3) {
          const [day, month, year] = parts
          lastNdtDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
          
          // Calculate next NDT date based on grade
          // Grade 100: 1 year interval, Grade 80 (or no grade): 4 year interval
          const ndtDateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
          const yearsToAdd = entry.grade === '100' ? 1 : 4
          ndtDateObj.setFullYear(ndtDateObj.getFullYear() + yearsToAdd)
          nextNdtDate = ndtDateObj.toISOString().split('T')[0]
          console.log(`NDT interval: Grade ${entry.grade || '80'} = ${yearsToAdd} years, next NDT: ${nextNdtDate}`)
        }
      }
      
      const equipmentData: Record<string, any> = {
        asset_number: assetNumber,
        serial_number: entry.serial_number.trim().toUpperCase(), // Always store in uppercase
        name: entry.name.trim(), // Equipment type name from Utstyrstype dropdown
        category_id: categoryV2Id, // Use categories_v2 ID, not samleliste_categories
        manufacturer_id: manufacturerV2Id, // Use manufacturers_v2 ID
        company_id: companyId,
        status: 'Aktiv',
        location: entry.location?.trim() || null,
        manufacture_year: entry.manufacture_year || null,
        last_inspection_date: now.toISOString(), // Set inspection date to today
        next_inspection_date: nextInspectionDate.toISOString() // Set next inspection to 1 year from now
      }
      
      // Add NDT dates if provided
      if (lastNdtDate) {
        equipmentData.last_ndt_date = lastNdtDate
      }
      if (nextNdtDate) {
        equipmentData.next_ndt_date = nextNdtDate
      }
      
      const { data: equipment, error } = await supabase
        .from('equipment_v2')
        .insert(equipmentData)
        .select('id, asset_number')
        .single()
      
      if (error) {
        throw error
      }
      
      // Save canonical specs (WLL, Prod Acc To, Manufacturer, Prod Year)
      await this.saveCanonicalSpecs(equipment.id, entry, manufacturerName, userId)
      
      console.log(`📄 Linking documents for ${entry.name}:`, {
        category_id: entry.category_id,
        manufacturer_id: entry.manufacturer_id,
        manufacturerName,
        product_type_id: (entry as any).product_type_id,
        wll: entry.wll,
        batch_id_bow: (entry as any).batch_id_bow,
        batch_id_bolt: (entry as any).batch_id_bolt
      })
      
      // Link default documents (manuals, type approvals, etc.)
      await this.linkDefaultDocuments(equipment.id, entry, userId)
      
      // Search and link Gunnebo certificates by WLL + serial pattern
      if (manufacturerName?.toLowerCase().includes('gunnebo')) {
        console.log(`🏷️ Attempting Gunnebo certificate link for ${entry.name}`)
        await this.linkGunneboCertificate(equipment.id, entry, userId)
      } else {
        console.log(`⏭️ Skipping Gunnebo certificate (manufacturer: ${manufacturerName})`)
      }
      
      return {
        success: true,
        equipment_id: equipment.id,
        asset_number: equipment.asset_number,
        // Use entry.standard (from product type) or category standards from DB
        categoryStandards: entry.standard || categoryStandards || undefined
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Ukjent feil'
      }
    }
  }

  /**
   * Link default documents (manuals, type approvals) to equipment
   * Uses virtual storage paths to link to shared documents in Supabase storage
   */
  private static async linkDefaultDocuments(
    equipmentId: string,
    entry: EquipmentEntry,
    userId: string
  ): Promise<void> {
    try {
      let productTypeId = (entry as any).product_type_id
      
      // Track samleliste IDs for document lookup (product_default_documents uses samleliste IDs)
      let samlelisteCatId: string | null = null
      let samlelisteMfrId: string | null = null
      
      // If no product_type_id, try to find one by matching equipment name
      // This is important for inventory splits where product_type wasn't preserved
      if (!productTypeId && entry.name && entry.manufacturer_id && entry.category_id) {
        console.log(`📚 Looking up product type for ${entry.name} (cat: ${entry.category_id}, mfr: ${entry.manufacturer_id})`)
        
        // First, get the category code from categories_v2
        const { data: catV2 } = await supabase
          .from('categories_v2')
          .select('code')
          .eq('id', entry.category_id)
          .single()
        
        if (catV2?.code) {
          // Map category code to samleliste category code (R4-2 -> R4)
          const samlelisteCatCode = catV2.code.split('-')[0] // R4-2 becomes R4
          console.log(`📚 Category code: ${catV2.code} -> samleliste: ${samlelisteCatCode}`)
          
          // Find the samleliste category by code
          const { data: samlelisteCat } = await supabase
            .from('samleliste_categories')
            .select('id')
            .eq('code', samlelisteCatCode)
            .single()
          
          if (samlelisteCat) {
            samlelisteCatId = samlelisteCat.id
            console.log(`📚 Found samleliste_category: ${samlelisteCat.id}`)
            
            // Get manufacturer name from manufacturers_v2 to find matching samleliste_manufacturer
            const { data: mfrV2 } = await supabase
              .from('manufacturers_v2')
              .select('name')
              .eq('id', entry.manufacturer_id)
              .single()
            
            if (mfrV2?.name) {
              // Find matching samleliste_manufacturer by name
              const { data: samMfr } = await supabase
                .from('samleliste_manufacturers')
                .select('id')
                .ilike('name', `%${mfrV2.name}%`)
                .limit(1)
                .single()
              
              if (samMfr) {
                samlelisteMfrId = samMfr.id
                console.log(`📚 Found samleliste_manufacturer: ${samMfr.id} for ${mfrV2.name}`)
              }
            }
            
            // Look up product types for this category and samleliste manufacturer
            const { data: productTypes } = samlelisteMfrId ? await supabase
              .from('samleliste_product_types')
              .select('id, type_name, type_code')
              .eq('category_id', samlelisteCat.id)
              .eq('manufacturer_id', samlelisteMfrId)
              .eq('is_active', true) : { data: null }
            
            console.log(`📚 Found ${productTypes?.length || 0} product types for category ${samlelisteCat.id}, manufacturer ${samlelisteMfrId}`)
            
            if (productTypes && productTypes.length > 0) {
              // Try to match by equipment name
              const nameLower = entry.name.toLowerCase()
              const firstWord = nameLower.split(' ')[0]
              
              const matchedType = productTypes.find(pt => 
                nameLower.includes(pt.type_name.toLowerCase()) ||
              pt.type_name.toLowerCase().includes(firstWord) ||
              (pt.type_code && nameLower.includes(pt.type_code.toLowerCase()))
            )
            
            if (matchedType) {
              productTypeId = matchedType.id
              console.log(`📋 Found matching product type: ${matchedType.type_name} (${matchedType.id})`)
            }
          }
        }
      }
    }
      
      // Build OR conditions for applicable documents
      // Include both v2 IDs and samleliste IDs since product_default_documents may use either
      const orConditions: string[] = []
      if (entry.manufacturer_id) {
        orConditions.push(`manufacturer_id.eq.${entry.manufacturer_id}`)
      }
      if (samlelisteMfrId) {
        orConditions.push(`manufacturer_id.eq.${samlelisteMfrId}`)
      }
      if (entry.category_id) {
        orConditions.push(`category_id.eq.${entry.category_id}`)
      }
      if (samlelisteCatId) {
        orConditions.push(`category_id.eq.${samlelisteCatId}`)
      }
      if (productTypeId) {
        orConditions.push(`product_type_id.eq.${productTypeId}`)
      }
      
      console.log(`📚 Document query conditions:`, orConditions)
      
      if (orConditions.length === 0) {
        return
      }
      
      // Find applicable default documents
      const { data: defaultDocs, error: queryError } = await supabase
        .from('product_default_documents')
        .select('*')
        .eq('is_active', true)
        .or(orConditions.join(','))
        .order('priority', { ascending: false })

      if (queryError) {
        console.log('📚 Default docs query error:', queryError)
        return
      }

      console.log(`📚 Found ${defaultDocs?.length || 0} default documents for equipment ${entry.name}`)
      if (!defaultDocs || defaultDocs.length === 0) return

      // Group by document_type and take highest priority for each
      const docsByType = new Map<string, typeof defaultDocs[0]>()
      for (const doc of defaultDocs) {
        if (!docsByType.has(doc.document_type)) {
          docsByType.set(doc.document_type, doc)
        }
      }

      // Create equipment_documents entries with virtual storage paths
      // Virtual path format: {real_storage_path}#{equipment_id} 
      // This allows multiple equipment to share the same physical file
      for (const doc of docsByType.values()) {
        const virtualStoragePath = `${doc.storage_path}#${equipmentId}`
        
        const { error: insertError } = await supabase.from('equipment_documents').insert({
          equipment_id: equipmentId,
          document_type: doc.document_type,
          storage_path: virtualStoragePath,
          file_name: doc.document_name,
          version: '1.0',
          is_current: true,
          uploaded_by: userId
        })
        
        if (insertError) {
          console.log(`📚 Could not link document ${doc.document_name}:`, insertError)
        } else {
          console.log(`📚 Linked document: ${doc.document_name} (${doc.document_type})`)
        }
      }
    } catch (err) {
      console.log('📚 Document linking error:', err)
      // Don't fail equipment creation if document linking fails
    }
  }

  /**
   * Search for and link Gunnebo certificate based on WLL + serial number pattern
   * Certificate filenames: "A085613 - 2T - RHO-B197.pdf"
   * We match by WLL (2T) and serial (RHO-B197 from batch_id_bow + batch_id_bolt)
   */
  private static async linkGunneboCertificate(
    equipmentId: string,
    entry: EquipmentEntry,
    userId: string
  ): Promise<void> {
    try {
      const batchBow = (entry as any).batch_id_bow?.trim()
      const batchBolt = (entry as any).batch_id_bolt?.trim()
      const wll = entry.wll?.trim()
      
      console.log(`🔍 Gunnebo cert check: bow=${batchBow}, bolt=${batchBolt}, wll=${wll}`)
      
      if (!batchBow || !batchBolt || !wll) {
        console.log(`❌ Gunnebo cert skipped: missing data (bow=${!!batchBow}, bolt=${!!batchBolt}, wll=${!!wll})`)
        return
      }

      // Construct the serial number pattern: "BGO-10350"
      const serialPattern = `${batchBow}-${batchBolt}`.toUpperCase()
      
      // Normalize WLL - create multiple variants to match
      // Input: "3.25t" -> variants: "3.25T", "3,25T", "3 25T"
      const wllBase = wll.replace('t', '').replace('T', '').trim()
      const wllVariants = [
        wllBase.toUpperCase() + 'T',                    // 3.25T
        wllBase.replace('.', ',').toUpperCase() + 'T',  // 3,25T
        wllBase.replace('.', ' ').toUpperCase() + 'T',  // 3 25T
        wllBase.replace(',', '.').toUpperCase() + 'T',  // in case input has comma
        wllBase.replace(',', ' ').toUpperCase() + 'T',  // in case input has comma
      ]
      
      // Query storage.objects directly to bypass RLS issues with list()
      // Search for files matching the serial pattern in the certificate folder
      const { data: storageObjects, error: queryError } = await supabase
        .from('storage_objects_view')
        .select('name')
        .eq('bucket_id', 'product')
        .like('name', `gunnebo/arctic_856/%${serialPattern}%`)
        .limit(10)

      // Fallback: try listing via storage API if view doesn't exist
      let files: { name: string }[] = []
      
      if (queryError) {
        const { data: listData, error: listError } = await supabase.storage
          .from('product')
          .list('gunnebo/arctic_856', { limit: 1000 })
        
        if (listError) {
          return
        }
        files = listData || []
      } else {
        // Extract just the filename from full path
        files = (storageObjects || []).map(obj => ({
          name: obj.name.split('/').pop() || obj.name
        }))
      }

      if (files.length === 0) {
        return
      }

      // Find a file matching both WLL and serial pattern
      // Filename format: "A085616-3,25T-BGO-10350.pdf"
      const matchingFile = files.find(file => {
        const filename = file.name.toUpperCase()
        
        // Check if filename contains the serial pattern (e.g., "BGO-10350")
        const hasSerial = filename.includes(serialPattern)
        if (!hasSerial) return false
        
        // Check if filename contains any WLL variant
        const hasWll = wllVariants.some(variant => {
          // Match patterns like "-3,25T-" or "-3.25T-" or " 3,25T "
          return filename.includes(`-${variant}-`) || 
                 filename.includes(` ${variant} `) ||
                 filename.includes(`-${variant}.`) ||  // at end before extension
                 filename.includes(` ${variant}.`)
        })
        
        return hasWll
      })

      if (!matchingFile) {
        return
      }

      // Storage path for 'product' bucket - prefix with bucket name for document viewer
      // Format: {bucket}:{path} so viewer knows which bucket to use
      const storagePath = `product:gunnebo/arctic_856/${matchingFile.name}`
      const virtualStoragePath = `${storagePath}#${equipmentId}`

      // Link the certificate to equipment
      const { error: insertError } = await supabase.from('equipment_documents').insert({
        equipment_id: equipmentId,
        document_type: 'certificate',
        storage_path: virtualStoragePath,
        file_name: matchingFile.name,
        version: '1.0',
        is_current: true,
        uploaded_by: userId
      })

      if (insertError) {
        // Could not link Gunnebo certificate
      }
    } catch {
      // Don't fail equipment creation if certificate linking fails
    }
  }

  /**
   * Save canonical specifications for equipment
   * Uses canonical_specification_templates for proper spec storage
   */
  private static async saveCanonicalSpecs(
    equipmentId: string,
    entry: ExtendedEquipmentEntry,
    manufacturerName: string | null,
    userId: string
  ): Promise<void> {
    // Canonical template IDs from canonical_specification_templates
    const TEMPLATE_IDS = {
      wll: 'f09e9789-c92d-42d4-a91e-a22e20eb04d3',
      dim_mm: '07faa69f-a861-43c8-892b-910067337861',
      thread_type: '6b9d107b-b305-42de-848b-14165be14e73',  // Gjengetype for eye bolts
      prod_acc_to: '11ad6cce-4f7d-4658-b097-24567833ffba',
      manufacturer: '1515569d-7b42-4319-8195-4498eddc3fc3',
      prod_year: '72bb2557-72be-422c-b0b3-59b78f0dbf7f',
      grade: 'e5c3794b-c0cd-4bdb-95ad-2022cf48db10',
      quantity: 'f62aa9ac-2b85-4363-b08e-1e5ece8c291f',
      batch_id_bow: '3cc73892-3a10-4cd3-9d60-4e3e3535ac3f',
      batch_id_bolt: '9edf6f36-a250-4338-a6e1-89bea931f512',
      capacity: '6cca33f3-e56a-4b0c-9f34-fe4b4f08fe23',  // Kapasitet (kg) for FAL categories
      length_m: 'cbfcde41-eeb9-4f86-9c23-20c842fb980e',  // Length (m) for Fiber Slings (R2)
      hook_id: '8ecc62ff-018c-482b-85a2-9a00bf39af22'    // Hook ID/serial for Fiber Slings (R2)
    }
    
    // WLL unit ID (tonnes)
    const WLL_UNIT_ID = 'f7834645-57af-4454-b4fb-a4bd47f8a424'
    // Capacity unit ID (kg) for FAL categories
    const CAPACITY_UNIT_ID = '44044297-f666-4952-a1bb-72b96f24c68c'
    // Length unit ID (meters) for Fiber Slings
    const LENGTH_UNIT_ID = '0a88f79f-8243-42fb-8028-a6fa625814e9'

    interface SpecRecord {
      equipment_id: string
      template_id: string
      spec_key: string
      value_text?: string | null
      value_numeric?: number | null
      value_integer?: number | null
      unit_id?: string | null
      created_by: string
      is_validated: boolean
      version: number
    }

    const specs: SpecRecord[] = []

    // WLL - parse numeric value from string like "8.5t" or "0,75 (M12)"
    if (entry.wll) {
      // Strip parentheses part first (e.g., "0,75 (M12)" -> "0,75")
      // Then replace comma with period for Norwegian format (e.g., "0,75" -> "0.75")
      // Then remove 't' suffix
      const wllClean = entry.wll
        .replace(/\s*\(.*\)$/, '')  // Remove parentheses and contents
        .replace(',', '.')           // Norwegian decimal to standard
        .replace('t', '')            // Remove 't' suffix
        .trim()
      const wllNumeric = parseFloat(wllClean)
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.wll,
        spec_key: 'wll',
        value_numeric: isNaN(wllNumeric) ? null : wllNumeric,
        unit_id: WLL_UNIT_ID,
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    // Capacity (kg) - for FAL (Fall Protection) categories
    if (entry.capacity && entry.capacity > 0) {
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.capacity,
        spec_key: 'capacity',
        value_integer: entry.capacity,
        unit_id: CAPACITY_UNIT_ID,
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    // Length (m) - for Fiber Sling (R2) categories
    if (entry.length) {
      const lengthClean = entry.length
        .replace(',', '.')  // Norwegian decimal to standard
        .replace('m', '')   // Remove 'm' suffix if present
        .trim()
      const lengthNumeric = parseFloat(lengthClean)
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.length_m,
        spec_key: 'length_m',
        value_numeric: isNaN(lengthNumeric) ? null : lengthNumeric,
        unit_id: LENGTH_UNIT_ID,
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    // Hook ID - for Fiber Sling (R2) categories with hook attached
    if (entry.hasHook && entry.hookId) {
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.hook_id,
        spec_key: 'hook_id',
        value_text: entry.hookId.trim().toUpperCase(),
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    // Dimension (auto-filled based on WLL)
    // For eye bolts: thread type like "M12", "M16", or "3/8-16 UNC" -> use thread_type spec
    // For shackles: dimension like "13 mm" -> use dim_mm spec
    if (entry.dimension) {
      const dimValue = entry.dimension.trim()
      // Check if it's a thread type (starts with "M" followed by number, or contains "UNC")
      const isThreadType = /^M\d+/.test(dimValue) || dimValue.includes('UNC')
      
      if (isThreadType) {
        // Thread type for eye bolts (store as text)
        specs.push({
          equipment_id: equipmentId,
          template_id: TEMPLATE_IDS.thread_type,
          spec_key: 'thread_type',
          value_text: dimValue,
          created_by: userId,
          is_validated: false,
          version: 1
        })
      } else {
        // Dimension in mm for shackles (store as numeric)
        const dimNumeric = parseFloat(dimValue.replace('mm', '').trim())
        specs.push({
          equipment_id: equipmentId,
          template_id: TEMPLATE_IDS.dim_mm,
          spec_key: 'dim_mm',
          value_numeric: isNaN(dimNumeric) ? null : dimNumeric,
          created_by: userId,
          is_validated: false,
          version: 1
        })
      }
    }

    // Produced According To (Standard - e.g., EN-13889)
    if (entry.standard) {
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.prod_acc_to,
        spec_key: 'prod_acc_to',
        value_text: entry.standard,
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    // Manufacturer
    if (manufacturerName) {
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.manufacturer,
        spec_key: 'manufacturer',
        value_text: manufacturerName,
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    // Production Year (if available)
    if (entry.manufacture_year) {
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.prod_year,
        spec_key: 'prod_year',
        value_integer: entry.manufacture_year,
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    // Grade (e.g., "80" for Grade 80 shackles)
    if (entry.grade) {
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.grade,
        spec_key: 'grade',
        value_text: entry.grade,
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    // Quantity (Antall)
    if (entry.quantity && entry.quantity > 0) {
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.quantity,
        spec_key: 'quantity',
        value_integer: entry.quantity,
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    // Batch ID Bow (Bue)
    if (entry.batch_id_bow) {
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.batch_id_bow,
        spec_key: 'batch_id_bow',
        value_text: entry.batch_id_bow.trim(),
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    // Batch ID Bolt
    if (entry.batch_id_bolt) {
      specs.push({
        equipment_id: equipmentId,
        template_id: TEMPLATE_IDS.batch_id_bolt,
        spec_key: 'batch_id_bolt',
        value_text: entry.batch_id_bolt.trim(),
        created_by: userId,
        is_validated: false,
        version: 1
      })
    }

    if (specs.length > 0) {
      try {
        await supabase.from('equipment_specifications_v2').insert(specs)
      } catch (err) {
        console.error('Failed to save canonical specs:', err)
      }
    }
    
    // NDT Date (ndt_date) - for chain slings - insert separately with proper format
    if (entry.ndtDate) {
      // Parse dd.mm.yyyy to ISO date format for storage
      // Input format: dd.mm.yyyy (e.g., "03.01.2026" = January 3rd, 2026)
      const parts = entry.ndtDate.split('.')
      let isoDate: string | null = null
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0')
        const month = parts[1].padStart(2, '0')
        const year = parts[2]
        // ISO format: YYYY-MM-DD
        isoDate = `${year}-${month}-${day}`
        console.log(`NDT Date parsing: input="${entry.ndtDate}", day="${day}", month="${month}", year="${year}", isoDate="${isoDate}"`)
      }
      
      // Use the known template ID for ndt_date from canonical_specification_templates
      // Template ID: d0dd8453-662a-49af-8571-0d8a5a1deb20 (spec_key: 'ndt_date')
      const NDT_DATE_TEMPLATE_ID = 'd0dd8453-662a-49af-8571-0d8a5a1deb20'
      
      // Insert NDT date spec
      const ndtSpec = {
        equipment_id: equipmentId,
        template_id: NDT_DATE_TEMPLATE_ID,
        spec_key: 'ndt_date',
        value_text: null,
        value_numeric: null,
        value_integer: null,
        value_date: isoDate,
        value_boolean: null,
        value_reference_id: null,
        validation_status: 'pending',
        created_by: userId,
        is_validated: false,
        version: 1
      }
      
      try {
        const { error: ndtError } = await supabase
          .from('equipment_specifications_v2')
          .insert(ndtSpec)
        
        if (ndtError) {
          console.error('Failed to save NDT date spec:', ndtError)
        } else {
          console.log(`NDT date spec saved successfully: ${isoDate}`)
        }
      } catch (err) {
        console.error('Error saving NDT date spec:', err)
      }
    }
    
    // Custom spec values for Tilpasset categories
    if (entry.isCustomCategory && entry.customSpecValues && Object.keys(entry.customSpecValues).length > 0) {
      console.log('Saving custom spec values for Tilpasset category:', entry.customSpecValues)
      
      // Known unit IDs for specific spec keys
      const SPEC_UNIT_MAP: Record<string, string> = {
        'capacity': CAPACITY_UNIT_ID,  // kg
        'wll': WLL_UNIT_ID,            // tonnes
        'length_m': LENGTH_UNIT_ID,    // meters
      }
      
      // Look up template IDs for each spec_key
      const specKeys = Object.keys(entry.customSpecValues)
      const { data: templates } = await supabase
        .from('canonical_specification_templates')
        .select('id, spec_key, data_type, default_unit_id')
        .in('spec_key', specKeys)
      
      if (templates && templates.length > 0) {
        const customSpecs: SpecRecord[] = []
        
        for (const template of templates) {
          const value = entry.customSpecValues[template.spec_key]
          if (!value || value.trim() === '') continue
          
          // Skip manufacturer (handled separately via customManufacturerName)
          if (['manufacturer'].includes(template.spec_key)) continue
          
          const specRecord: SpecRecord = {
            equipment_id: equipmentId,
            template_id: template.id,
            spec_key: template.spec_key,
            created_by: userId,
            is_validated: false,
            version: 1
          }
          
          // Add unit_id if known for this spec key, or use template's default
          const unitId = SPEC_UNIT_MAP[template.spec_key] || template.default_unit_id
          if (unitId) {
            specRecord.unit_id = unitId
          }
          
          // Set the appropriate value field based on data_type
          if (template.data_type === 'number' || template.data_type === 'decimal') {
            const numVal = parseFloat(value.replace(',', '.'))
            specRecord.value_numeric = isNaN(numVal) ? null : numVal
          } else if (template.data_type === 'integer') {
            const intVal = parseInt(value)
            specRecord.value_integer = isNaN(intVal) ? null : intVal
          } else {
            specRecord.value_text = value.trim()
          }
          
          customSpecs.push(specRecord)
        }
        
        if (customSpecs.length > 0) {
          try {
            const { error: customError } = await supabase
              .from('equipment_specifications_v2')
              .insert(customSpecs)
            
            if (customError) {
              console.error('Failed to save custom specs:', customError)
            } else {
              console.log(`Saved ${customSpecs.length} custom specs for Tilpasset category`)
            }
          } catch (err) {
            console.error('Error saving custom specs:', err)
          }
        }
      }
    }
  }

  /**
   * Update existing equipment specs in the database
   * Used when user edits specs for existing equipment before generating control card
   */
  private static async updateExistingSpecs(
    equipmentId: string,
    editedSpecs: Array<{ spec_key: string; value: string }>,
    userId: string
  ): Promise<void> {
    try {
      for (const spec of editedSpecs) {
        // Check if spec exists
        const { data: existing } = await supabase
          .from('equipment_specifications_v2')
          .select('id')
          .eq('equipment_id', equipmentId)
          .eq('spec_key', spec.spec_key)
          .single()
        
        if (existing) {
          // Update existing spec with the new value as text
          // This replaces any UUID reference with the actual resolved text
          await supabase
            .from('equipment_specifications_v2')
            .update({
              value_text: spec.value,
              value_reference_id: null, // Clear reference ID since we now have resolved text
              updated_by: userId,
              updated_at: new Date().toISOString()
            })
            .eq('id', existing.id)
        }
      }
    } catch (error) {
      console.error('Failed to update existing specs:', error)
    }
  }

  /**
   * Generate control card for equipment
   * @param standard - The standard the equipment was produced according to (e.g., EN-13889 for shackles)
   * @param inspectionStatus - Inspection result (OK, C, MO, RC, NC)
   * @param remarks - Remarks/comments when status is not OK
   * @param inspectionPhoto - Base64 data URL for photo of defect (for non-OK statuses)
   * @param orderNo - Order number to save to equipment and display on control card
   * @param location - Location to save to equipment and display on control card
   */
  private static async generateControlCard(
    equipmentId: string,
    performedByName: string,
    responsiblePersonName: string,
    performedBySignature?: string,
    responsiblePersonSignature?: string,
    standard?: string,
    inspectionStatus?: string,
    remarks?: string,
    inspectionPhoto?: string,
    orderNo?: string,
    location?: string
  ): Promise<jsPDF | null> {
    try {
      const container = getContainer()
      const useCase = container.getGenerateControlCardUseCase()
      
      // Base regulations always included
      const baseRegulations = 'Forskrift Nr 1357, Forskrift Nr 1360'
      
      // Add applicable standard if available (e.g., EN-13889 for offshore shackles)
      const controlledAgainst = standard 
        ? `${baseRegulations} & ${standard}` 
        : baseRegulations
      
      const result = await useCase.execute({
        equipmentId,
        inspection: {
          jobType: 'Periodisk kontroll',
          performedDate: new Date().toISOString(),
          performedBy: performedByName,
          responsiblePerson: responsiblePersonName,
          performedBySignature,
          responsiblePersonSignature,
          controlledAgainst,
          status: inspectionStatus || 'OK',
          remarks: remarks || undefined
        },
        logos: {
          company: STM_LOGO,
          certification: CERT_LOGO
        },
        options: {
          finalize: true, // Generate proper control card, not draft
          includeSubEquipment: false,
          // Add inspection photo as second page for non-OK statuses
          attachmentImage: (inspectionStatus && inspectionStatus !== 'OK' && inspectionPhoto) 
            ? inspectionPhoto 
            : undefined
        }
      })
      
      if (result.success && result.pdf) {
        return result.pdf
      }
      return null
    } catch {
      return null
    }
  }

  /**
   * Upload control card PDF to storage
   */
  private static async uploadControlCard(
    pdf: jsPDF,
    equipmentId: string,
    serialNumber: string,
    companyId: string,
    inspectionStatus?: string
  ): Promise<void> {
    try {
      // Get company name for upload path
      const { data: companyData } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single()
      
      const companyName = companyData?.name || 'unknown'
      
      // Convert PDF to blob
      const pdfBlob = pdf.output('blob')
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0]
      const fileName = `${serialNumber}_kontrollkort_${timestamp}.pdf`
      
      // Convert blob to File object
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' })
      
      // Upload using standard upload service
      const uploadResult = await uploadDocument({
        file: pdfFile,
        customerName: companyName,
        serialNumber: serialNumber,
        equipmentId: equipmentId,
        documentType: 'control_card',
        inspectionStatus: inspectionStatus || 'OK'
      })
      
      // Upload handled by service
    } catch {
      // Error uploading control card
    }
  }

  /**
   * Generate MT/MPI report for chain sling NDT inspection
   * @param entry - Equipment entry with NDT data
   * @param companyId - Company ID for report association
   * @param inspectorName - Name of the inspector issuing the report
   * @param responsiblePersonName - Name of the responsible person (ordreansvarlig/verifier)
   * @param equipmentId - Equipment ID to associate the report with
   * @returns The generated PDF blob for inclusion in combined download
   */
  private static async generateMTReport(
    entry: EquipmentEntry,
    companyId: string,
    inspectorName: string,
    responsiblePersonName?: string,
    equipmentId?: string
  ): Promise<Blob | null> {
    try {
      // Get company info for report
      const { data: company } = await supabase
        .from('companies')
        .select('name, report_code')
        .eq('id', companyId)
        .single()
      
      if (!company) {
        console.error('Company not found for MT report generation')
        return null
      }
      
      // Generate report number: I{YY}{MM}{report_code}
      const now = new Date()
      const year = now.getFullYear().toString().slice(-2)
      const month = (now.getMonth() + 1).toString().padStart(2, '0')
      const reportNumber = `I${year}${month}${(company.report_code || '001').padStart(3, '0')}`
      
      // Parse NDT date (dd.mm.yyyy) to ISO date string
      let testDate = now.toISOString().split('T')[0] // Default to today
      if (entry.ndtDate) {
        const parts = entry.ndtDate.split('.')
        if (parts.length === 3) {
          const [day, monthStr, yearStr] = parts
          testDate = `${yearStr}-${monthStr.padStart(2, '0')}-${day.padStart(2, '0')}`
        }
      }
      
      // Get sequence number for this report folder
      const { data: existingFiles } = await supabase
        .storage
        .from('reports')
        .list(reportNumber)
      
      const sequenceNumber = existingFiles ? existingFiles.length + 1 : 1
      const paddedSequence = String(sequenceNumber).padStart(3, '0')
      
      // Prepare MT report data with approval info
      const mtReportData: MTReportData = {
        testDate,
        reportNumber,
        objects: [entry.serial_number.trim().toUpperCase()], // Serial number as Object ID
        objectDescription: entry.name, // Utstyrstype as Object Description
        issuedBy: inspectorName,
        status: 'Accepted', // Default to Accepted for chain sling inspections
        surfaceCondition: 'Forged', // Chain slings are forged
        equipmentSettings: DEFAULT_MT_EQUIPMENT,
        certificationNumber: '---',
        sequenceNumber: paddedSequence,
        extentOfExamination: '100',
        material: 'Carbon steel',
        testProcedure: 'MT DNV-CG-0051',
        testAcceptance: 'DNV-CG-0051 Level 1',
        referenceStandards: '---',
        company: companyId,
        report_code: company.report_code,
        // Approval info - responsible person (ordreansvarlig) signature will be fetched by template
        approvedBy: responsiblePersonName,
        approvedAt: now.toISOString()
      }
      
      // Generate MT PDF
      const pdfBlob = await generateMTPDF(mtReportData)
      
      // Upload to storage
      const fileName = `${reportNumber}_${paddedSequence}.pdf`
      const folderPath = `${reportNumber}/${fileName}`
      
      const { error: uploadError } = await supabase
        .storage
        .from('reports')
        .upload(folderPath, pdfBlob, {
          contentType: 'application/pdf',
          cacheControl: '3600'
        })
      
      if (uploadError) {
        console.error('Error uploading MT report:', uploadError)
        return pdfBlob // Still return the blob even if upload failed
      }
      
      // Store metadata in ndt_reports table
      const { data: { user } } = await supabase.auth.getUser()
      const { error: metadataError } = await supabase
        .from('ndt_reports')
        .insert({
          order_number: reportNumber,
          unit_id: entry.serial_number.trim().toUpperCase(),
          test_date: testDate,
          test_type: 'MT',
          status: 'Accepted',
          issued_by: inspectorName,
          verified_by: responsiblePersonName || null, // Include ordreansvarlig
          file_path: folderPath,
          report_code: company.report_code,
          company_id: companyId,
          created_by: user?.id,
          report_data: {
            ...mtReportData,
            sequenceNumber: paddedSequence
          }
        })
      
      if (metadataError) {
        console.error('Error storing MT report metadata:', metadataError)
      }
      
      // Also upload MT report to equipment documents if equipment ID is provided
      if (equipmentId) {
        try {
          await uploadDocument({
            file: new File([pdfBlob], fileName, { type: 'application/pdf' }),
            customerName: company.name,
            serialNumber: entry.serial_number.trim().toUpperCase(),
            equipmentId,
            documentType: 'ndt_report'
          })
        } catch (docError) {
          console.error('Error uploading MT report to equipment documents:', docError)
        }
        
        // Calculate next NDT date based on grade
        // Grade 100: 1 year interval, Grade 80 (or no grade): 4 year interval
        const ndtDateObj = new Date(testDate)
        const yearsToAdd = entry.grade === '100' ? 1 : 4
        ndtDateObj.setFullYear(ndtDateObj.getFullYear() + yearsToAdd)
        const nextNdtDate = ndtDateObj.toISOString().split('T')[0]
        console.log(`MT Report: Grade ${entry.grade || '80'} = ${yearsToAdd} year NDT interval, next NDT: ${nextNdtDate}`)
        
        // Update last_ndt_date and next_ndt_date in equipment table
        const { error: ndtDateError } = await supabase
          .from('equipment_v2')
          .update({ 
            last_ndt_date: testDate,
            next_ndt_date: nextNdtDate,
            updated_at: now.toISOString()
          })
          .eq('id', equipmentId)
        
        if (ndtDateError) {
          console.error('Error updating NDT dates:', ndtDateError)
        }
      }
      
      // Return the generated PDF blob for combined download
      return pdfBlob
    } catch (error) {
      console.error('Error generating MT report:', error)
      return null
    }
  }

  /**
   * Create multiple equipment records with optional control card generation
   */
  static async createBulkEquipment(
    entries: EquipmentEntry[],
    options: BulkCreationOptions,
    onProgress?: (current: number, total: number, message: string) => void
  ): Promise<BulkCreationResult> {
    const { 
      companyId, 
      generateControlCards, 
      inspectorId, 
      inspectorName,
      inspectorSignature,
      responsiblePersonName,
      responsiblePersonSignature,
      orderNo,
      serviceOrder, // TechnipFMC: if set, uses this instead of orderNo
      location: flowLocation
    } = options
    
    // Use serviceOrder if provided (TechnipFMC), otherwise use orderNo
    const effectiveOrderNo = serviceOrder || orderNo
    const created: BulkCreationResult['created'] = []
    const failed: BulkCreationResult['failed'] = []
    const controlCardPDFs: jsPDF[] = []
    const mtReportBlobs: Blob[] = [] // Collect MT report blobs for combined download
    
    const total = entries.length
    
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i]
      
      // If generateNdtReport is checked, set ndtDate to today (used for control card and equipment creation)
      if (entry.generateNdtReport && !entry.ndtDate) {
        const today = new Date()
        const day = today.getDate().toString().padStart(2, '0')
        const month = (today.getMonth() + 1).toString().padStart(2, '0')
        const year = today.getFullYear().toString()
        entry.ndtDate = `${day}.${month}.${year}`
      }
      
      // Check if this is an existing equipment (just generate control card, no creation)
      if (entry.existing_equipment_id) {
        onProgress?.(i + 1, total, `Oppdaterer ${entry.name}...`)
        
        // Get existing equipment's asset number
        const { data: existingEq } = await supabase
          .from('equipment_v2')
          .select('asset_number, category_id')
          .eq('id', entry.existing_equipment_id)
          .single()
        
        if (existingEq) {
          // Samleliste uses base regulations only: Forskrift Nr 1357, Forskrift Nr 1360
          const existingCategoryStandards: string | null = null
          // Update inspection dates and location in equipment_v2 table
          const now = new Date()
          const nextInspectionDate = new Date(now)
          nextInspectionDate.setFullYear(nextInspectionDate.getFullYear() + 1) // Default 1 year interval
          
          const updateData: Record<string, any> = {
            last_inspection_date: now.toISOString(),
            next_inspection_date: nextInspectionDate.toISOString(),
            updated_at: now.toISOString()
          }
          
          // Update location if provided
          if (entry.location?.trim()) {
            updateData.location = entry.location.trim()
          }
          
          // Update NDT dates if generateNdtReport is checked (uses today's date)
          if (entry.generateNdtReport && entry.ndtDate) {
            const parts = entry.ndtDate.split('.')
            if (parts.length === 3) {
              const [day, month, year] = parts
              const lastNdtDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
              
              // Calculate next NDT date based on grade
              // Grade 100: 1 year interval, Grade 80 (or no grade): 4 year interval
              const ndtDateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
              const yearsToAdd = entry.grade === '100' ? 1 : 4
              ndtDateObj.setFullYear(ndtDateObj.getFullYear() + yearsToAdd)
              const nextNdtDate = ndtDateObj.toISOString().split('T')[0]
              
              updateData.last_ndt_date = lastNdtDate
              updateData.next_ndt_date = nextNdtDate
            }
          }
          
          const { error: updateError } = await supabase
            .from('equipment_v2')
            .update(updateData)
            .eq('id', entry.existing_equipment_id)
          
          if (updateError) {
            console.error('Failed to update equipment dates:', updateError)
          }
          
          // Update edited specs in the database before generating control card
          if (entry.edited_specs && entry.edited_specs.length > 0) {
            await this.updateExistingSpecs(entry.existing_equipment_id, entry.edited_specs, inspectorId)
          }
          
          created.push({
            entry_id: entry.id,
            equipment_id: entry.existing_equipment_id,
            asset_number: existingEq.asset_number || ''
          })
          
          // Generate control card for existing equipment
          if (generateControlCards) {
            onProgress?.(i + 1, total, `Genererer kontrollkort for ${entry.name}...`)
            // Use entry.standard (if set) or category standards from DB
            const standardsToUse = entry.standard || existingCategoryStandards || undefined
            const pdf = await this.generateControlCard(
              entry.existing_equipment_id,
              inspectorName,
              responsiblePersonName || inspectorName,
              inspectorSignature,
              responsiblePersonSignature,
              standardsToUse,
              entry.inspectionStatus,
              entry.remarks,
              entry.inspectionPhoto,
              effectiveOrderNo,
              entry.location || flowLocation
            )
            if (pdf) {
              controlCardPDFs.push(pdf)
              
              // Upload control card to storage
              await this.uploadControlCard(
                pdf,
                entry.existing_equipment_id,
                entry.serial_number.trim().toUpperCase(),
                companyId,
                entry.inspectionStatus
              )
            }
          }
          
          // Generate MT/MPI report if requested (for chain slings)
          if (entry.generateNdtReport) {
            onProgress?.(i + 1, total, `Genererer NDT-rapport for ${entry.name}...`)
            const mtBlob = await this.generateMTReport(
              entry, 
              companyId, 
              inspectorName, 
              responsiblePersonName,
              entry.existing_equipment_id
            )
            if (mtBlob) {
              mtReportBlobs.push(mtBlob)
            }
          }
        } else {
          failed.push({ entry_id: entry.id, error: 'Eksisterende utstyr ikke funnet' })
        }
        continue
      }
      
      // NEW EQUIPMENT: Validate and create
      onProgress?.(i + 1, total, `Oppretter ${entry.name}...`)
      
      // Validate
      const errors = this.validateEntry(entry)
      if (errors.length > 0) {
        failed.push({ entry_id: entry.id, error: errors.join(', ') })
        continue
      }
      
      // Create equipment
      const result = await this.createSingleEquipment(entry, companyId, inspectorId)
      
      if (result.success && result.equipment_id && result.asset_number) {
        created.push({
          entry_id: entry.id,
          equipment_id: result.equipment_id,
          asset_number: result.asset_number
        })
        
        // Generate control card if requested
        if (generateControlCards) {
          onProgress?.(i + 1, total, `Genererer kontrollkort for ${entry.name}...`)
          // Pass performer and responsible person info for signatures, plus inspection result
          // Use category standards from DB (or entry.standard if set from product type)
          const pdf = await this.generateControlCard(
            result.equipment_id,
            inspectorName, // Performed by
            responsiblePersonName || inspectorName, // Responsible person (defaults to inspector if not admin)
            inspectorSignature,
            responsiblePersonSignature,
            result.categoryStandards, // Use standards from category lookup
            entry.inspectionStatus,
            entry.remarks,
            entry.inspectionPhoto,
            effectiveOrderNo,
            entry.location || flowLocation
          )
          if (pdf) {
            controlCardPDFs.push(pdf)
            
            // Upload control card to storage
            await this.uploadControlCard(
              pdf,
              result.equipment_id,
              entry.serial_number.trim().toUpperCase(),
              companyId,
              entry.inspectionStatus
            )
          }
        }
        
        // Generate MT/MPI report if requested (for chain slings)
        if (entry.generateNdtReport) {
          onProgress?.(i + 1, total, `Genererer NDT-rapport for ${entry.name}...`)
          const mtBlob = await this.generateMTReport(
            entry, 
            companyId, 
            inspectorName, 
            responsiblePersonName,
            result.equipment_id
          )
          if (mtBlob) {
            mtReportBlobs.push(mtBlob)
          }
        }
      } else {
        failed.push({ entry_id: entry.id, error: result.error || 'Ukjent feil' })
      }
    }
    
    // Merge all PDFs (control cards + MT reports) using pdf-lib
    let controlCardBlob: Blob | undefined
    if (controlCardPDFs.length > 0 || mtReportBlobs.length > 0) {
      onProgress?.(total, total, 'Samler dokumenter...')
      controlCardBlob = await this.mergeAllPDFs(controlCardPDFs, mtReportBlobs)
    }
    
    return {
      success: failed.length === 0,
      created,
      failed,
      controlCardBlob
    }
  }

  /**
   * Merge control card PDFs and MT report blobs into a single PDF using pdf-lib
   */
  private static async mergeAllPDFs(controlCards: jsPDF[], mtReports: Blob[]): Promise<Blob> {
    try {
      const mergedPdf = await PDFDocument.create()
      
      // Add control cards (convert from jsPDF to pdf-lib)
      for (const pdf of controlCards) {
        const pdfBytes = pdf.output('arraybuffer')
        const pdfDoc = await PDFDocument.load(pdfBytes)
        const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices())
        pages.forEach(page => mergedPdf.addPage(page))
      }
      
      // Add MT reports (already Blob)
      for (const blob of mtReports) {
        const arrayBuffer = await blob.arrayBuffer()
        const pdfDoc = await PDFDocument.load(arrayBuffer)
        const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices())
        pages.forEach(page => mergedPdf.addPage(page))
      }
      
      const mergedBytes = await mergedPdf.save()
      return new Blob([mergedBytes as BlobPart], { type: 'application/pdf' })
    } catch (error) {
      console.error('Error merging PDFs:', error)
      // Fallback: return just the first control card if merging fails
      if (controlCards.length > 0) {
        return controlCards[0].output('blob')
      }
      if (mtReports.length > 0) {
        return mtReports[0]
      }
      return new Blob()
    }
  }
}

